/**
 * Decimal adjustment of a number.
 *
 * @param   {String}    type    The type of adjustment.
 * @param   {Number}    value   The number.
 * @param   {Integer}   exp     The exponent (the 10 logarithm of the adjustment base).
 * @returns {Number}            The adjusted value.
 */
export function decimalAdjust(type, value, exp) {
    // If the exp is undefined or zero...
    if (typeof exp === "undefined" || +exp === 0) {
        return Math[type](value);
    }
    value = +value;
    exp = +exp;
    // If the value is not a number or the exp is not an integer...
    if (isNaN(value) || !(typeof exp === "number" && exp % 1 === 0)) {
        return NaN;
    }
    // Shift
    value = value.toString().split("e");
    value = Math[type](+(value[0] + "e" + (value[1] ? +value[1] - exp : -exp)));
    // Shift back
    value = value.toString().split("e");
    return +(value[0] + "e" + (value[1] ? +value[1] + exp : exp));
}

export function round10(value, exp) {
    return decimalAdjust("round", value, exp);
}

/**
 * Formats a number with commas as thousands separators.
 * 
 * @param {number} number - The number to format.
 * @returns {string} - The formatted number as a string.
 */
export const formatNumberWithCommas = (number) => number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

/**
 * Finds the greatest number less than or equal to `num` that is divisible by `divider`.
 * 
 * @param {number} num - The starting number.
 * @param {number} divider - The divisor.
 * @returns {number} - The greatest number divisible by `divider`.
 */
export function greatestDivisibleBy(num, divider) {
    let divisible = num;
    while (divisible % divider !== 0) {
        divisible -= 1;
    }
    return divisible;
}

/**
 * Calculates the simplest ratio of two numbers.
 * 
 * @param {number} num_1 - The first number.
 * @param {number} num_2 - The second number.
 * @returns {string[]} - The ratio in string format as [num_1, num_2].
 */
export function calculateRatio(num_1, num_2) {
    for (let num = num_2; num > 1; num--) {
        if ((num_1 % num) == 0 && (num_2 % num) == 0) {
            num_1 = num_1 / num;
            num_2 = num_2 / num;
        }
    }
    return [`${num_1}`, `${num_2}`];
}

/**
 * Converts comma-separated numbers in object values into range format.
 * Sequential numbers are condensed into a range (e.g., "1,2,3" → "1-3").
 * Reverse order sequences are also handled (e.g., "9,8,7" → "9-7").
 * The character 'B' is preserved in its original position.
 * Numbers with directional suffixes (e.g., "3east", "7south") are grouped separately.
 * Directions represent rotation (e.g., "east" = 90 degrees, "south" = 180 degrees).
 * 
 * @param {Object} obj - An object where values are comma-separated strings of numbers, directions, and/or 'B'.
 * @returns {Object} A new object with modified values in range format, preserving 'B' and grouping directions separately.
 *
 * @example
 * convertObjectValuesToRanges({ a: "1,2,3,5,6", b: "4,3,2,1,8" });
 * // Returns: { a: "1-3,5-6", b: "4-1,8" }
 *
 * convertObjectValuesToRanges({ a: "1,2,B,5,6", b: "4,3,2,B,8" });
 * // Returns: { a: "1-2,B,5-6", b: "4-2,B,8" }
 * 
 * convertObjectValuesToRanges({ a: "1,2east,3east,B,5west,6", b: "5south,4west,3,2,1south,B,8" });
 * // Returns: { a: "1,2-3east,B,5west,6", b: "5south,4west,3-2,1south,B,8" }
 */
export function convertObjectValuesToRanges(obj) {
    
    function convertToRangeString(str) {
        if (!str) return ''
        let items = str.split(',').map(item => {
            let match = item.match(/^(\d+)([a-z]*)$/i);
            return match ? { num: Number(match[1]), dir: match[2] || null } : { num: 'B', dir: null };
        });

        let segments = [];
        let temp = [];

        for (let i = 0; i < items.length; i++) {
            let current = items[i];

            if (current.num === 'B') {
                if (temp.length > 0) {
                    segments.push(temp);
                    temp = [];
                }
                segments.push([current]);
            } else {
                if (
                    temp.length === 0 ||
                    (typeof temp[temp.length - 1].num === 'number' &&
                        (current.num === temp[temp.length - 1].num + 1 || current.num === temp[temp.length - 1].num - 1) &&
                        current.dir === temp[temp.length - 1].dir)
                ) {
                    temp.push(current);
                } else {
                    segments.push(temp);
                    temp = [current];
                }
            }
        }

        if (temp.length > 0) {
            segments.push(temp);
        }

        return segments
            .map(segment => {
                if (segment[0].num === 'B') return 'B';
                let first = segment[0], last = segment[segment.length - 1];
                let range = first.num !== last.num ? `${first.num}-${last.num}` : `${first.num}`;
                return first.dir ? `${range}${first.dir}` : range;
            })
            .join(',');
    }

    let newObj = {};
    for (let key in obj) {
        newObj[key] = convertToRangeString(obj[key]);
    }
    return newObj;
}
